@if (changeDetection) {
  {{ blink() }}
}
<mat-toolbar class="logobar" color="primary">
  @if (compactNavigation()) {
    <mat-toolbar-row class="compact-toolbar-row">
      <button
        mat-button
        routerLink="/"
        routerLinkActive="active"
        class="logo-button logo-button--compact"
        aria-label="SyRF home"
      >
        <mat-icon svgIcon="syrf-title" class="logo-icon" />
      </button>

      <div class="compact-toolbar-actions">
        @if (appIsUnstable) {
          <button
            mat-icon-button
            class="unstable-warning-btn"
            aria-label="Application warning"
            matTooltip="Application may be unstable due to an error. Consider refreshing the page."
            matTooltipClass="warning-tooltip"
            (click)="showErrorRecoveryDialog()"
          >
            <mat-icon color="warn" class="warning-icon">warning</mat-icon>
          </button>
        }

        <app-environment-chip
          (clicked)="openEnvironmentWarning()"
        ></app-environment-chip>

        <button
          mat-icon-button
          [matMenuTriggerFor]="compactNavigationMenu"
          aria-label="Open navigation menu"
          data-testid="compact-navigation-menu-trigger"
        >
          <mat-icon>menu</mat-icon>
          @if (isImpersonatingUser$ | async) {
            <span class="sr-only">Currently impersonating another user</span>
          }
        </button>
      </div>

      <mat-menu #compactNavigationMenu="matMenu">
        @if (authenticated$ | async) {
          <button mat-menu-item [routerLink]="['projects']">
            <mat-icon>folder</mat-icon>
            <span>Projects</span>
          </button>
        }

        @if ($underProjectRoute()) {
          <button mat-menu-item [matMenuTriggerFor]="compactResourcesMenu">
            <mat-icon>topic</mat-icon>
            <span>Resources</span>
          </button>
        } @else {
          <a
            mat-menu-item
            href="https://www.camarades.de"
            target="_blank"
            rel="noopener noreferrer"
          >
            <mat-icon>science</mat-icon>
            <span>Methodology</span>
          </a>
          <button mat-menu-item [routerLink]="['mission']">
            <mat-icon>flag</mat-icon>
            <span>Our Mission</span>
          </button>
          <button mat-menu-item [matMenuTriggerFor]="compactLibraryMenu">
            <mat-icon>local_library</mat-icon>
            <span>Library</span>
          </button>
          <a
            mat-menu-item
            href="https://www.ed.ac.uk/clinical-brain-sciences/research/camarades/meet-the-team"
            target="_blank"
            rel="noopener noreferrer"
          >
            <mat-icon>groups</mat-icon>
            <span>About Us</span>
          </a>
        }

        <button mat-menu-item [matMenuTriggerFor]="compactHelpMenu">
          <mat-icon>help</mat-icon>
          <span>Help</span>
        </button>

        @if (!(authenticated$ | async)) {
          <button
            mat-menu-item
            (click)="signIn()"
            [disabled]="!(authIsIdle$ | async)"
          >
            @if (authIsIdle$ | async) {
              <mat-icon>fingerprint</mat-icon>
              <span>Sign In / Register</span>
            } @else {
              <mat-spinner diameter="20"></mat-spinner>
              <span>Signing In...</span>
            }
          </button>
        }

        @if (authenticatedUser$ | async; as authenticatedUser) {
          <a mat-menu-item routerLink="manage">
            <mat-icon>account_circle</mat-icon>
            <span>My Account</span>
          </a>
          @if (isAdmin$ | async) {
            <button
              mat-menu-item
              [matMenuTriggerFor]="compactAdminMenu"
              data-testid="compact-admin-menu-trigger"
            >
              <mat-icon>admin_panel_settings</mat-icon>
              <span>Admin</span>
            </button>
          }
          <button mat-menu-item (click)="signOut()">
            <mat-icon>logout</mat-icon>
            <span>Sign Out</span>
          </button>
        }
      </mat-menu>

      <mat-menu #compactLibraryMenu="matMenu">
        <button mat-menu-item [routerLink]="['library']">
          Systematic Review Library
        </button>
        <button mat-menu-item [routerLink]="['protocols']">Protocols</button>
      </mat-menu>

      <mat-menu #compactResourcesMenu="matMenu">
        <a
          mat-menu-item
          href="https://www.camarades.de"
          target="_blank"
          rel="noopener noreferrer"
        >
          Methodology
        </a>
        <button mat-menu-item [routerLink]="['mission']">Our Mission</button>
        <button mat-menu-item [matMenuTriggerFor]="compactLibraryMenu">
          Library
        </button>
        <a
          mat-menu-item
          href="https://www.ed.ac.uk/clinical-brain-sciences/research/camarades/meet-the-team"
          target="_blank"
          rel="noopener noreferrer"
        >
          About Us
        </a>
      </mat-menu>

      <mat-menu #compactHelpMenu="matMenu">
        <button mat-menu-item [routerLink]="['faq']">
          Frequently Asked Questions
        </button>
        <a
          mat-menu-item
          [attr.href]="'' | userGuideUrl"
          target="_blank"
          rel="noopener noreferrer"
        >
          User Guide
        </a>
        <button mat-menu-item [routerLink]="['about']">
          About Our Website
        </button>
        <button mat-menu-item (click)="openVersionInfo()">
          Version Information
        </button>
        <button mat-menu-item [routerLink]="['contact-us']">Contact Us</button>
      </mat-menu>

      <mat-menu #compactAdminMenu="matMenu">
        <button mat-menu-item (click)="toggleAdminUiOptions()">
          {{ showingAdminUiOptions() ? 'Hide' : 'Show' }} Admin UI Options
        </button>
        <button mat-menu-item [routerLink]="['admin']">
          User Impersonation
        </button>
        <a
          mat-menu-item
          routerLink="/admin/feature-flags"
          data-testid="compact-admin-feature-flags"
        >
          Feature Flags
        </a>
        <button mat-menu-item (click)="updateAllProjectInclusion()">
          Update All Project Inclusion
        </button>
        @if (currentProject()) {
          <button mat-menu-item (click)="updateAnnotationAnswerTally()">
            Update Annotation Answer Tally For Current Project
          </button>
        }
        @if (!(showDebuggers$ | async)) {
          <button mat-menu-item (click)="setDebuggers(true)">Debuggers</button>
        } @else {
          <button mat-menu-item (click)="setDebuggers(false)">
            Debuggers
            <mat-icon class="menu-check">check</mat-icon>
          </button>
        }
        @if (!(showLogging$ | async)) {
          <button mat-menu-item (click)="setLogging(true)">Diagnostics</button>
        } @else {
          <button mat-menu-item (click)="setLogging(false)">
            Diagnostics
            <mat-icon class="menu-check">check</mat-icon>
          </button>
        }
        @if (!(showChangeDetection$ | async)) {
          <button mat-menu-item (click)="setChangeDetectionVisualisation(true)">
            Change Detection Visualisation
          </button>
        } @else {
          <button
            mat-menu-item
            (click)="setChangeDetectionVisualisation(false)"
          >
            Change Detection Visualisation
            <mat-icon class="menu-check">check</mat-icon>
          </button>
        }
        <button mat-menu-item (click)="openGuidDecoder()">
          Guid Decoder/Encoder...
        </button>
        <button mat-menu-item (click)="onThrowError()">Throw Error</button>
        <button mat-menu-item disabled>{{ randomId }}</button>
      </mat-menu>
    </mat-toolbar-row>
  }

  @if (compactNavigation() && $underProjectRoute()) {
    <mat-toolbar-row class="compact-context-row">
      <button
        mat-icon-button
        type="button"
        class="project-navigation-trigger"
        [attr.aria-expanded]="projectNavigation.open()"
        aria-controls="project-navigation-drawer"
        [attr.aria-label]="
          projectNavigation.open()
            ? 'Close project navigation'
            : 'Open project navigation'
        "
        matTooltip="Project navigation"
        data-testid="project-navigation-trigger"
        (click)="toggleProjectNavigation()"
      >
        <mat-icon>{{
          projectNavigation.open() ? 'close' : 'menu_open'
        }}</mat-icon>
      </button>

      @if (authenticated$ | async) {
        <button
          class="compact-project-selector"
          mat-button
          [matMenuTriggerFor]="compactProjectsMenu"
          [attr.title]="currentProject()?.name"
        >
          <mat-icon class="compact-project-icon">folder</mat-icon>
          <span class="context-prefix">Project:</span>
          <span class="context-value">
            {{ currentProject()?.name || 'Current project' }}
          </span>
          <mat-icon iconPositionEnd>keyboard_arrow_down</mat-icon>
        </button>
      }

      @if ($underStageRoute()) {
        <span class="compact-stage-context" [attr.title]="currentStage()?.name">
          <span class="separator" aria-hidden="true">/</span>
          <span class="context-prefix">Stage:</span>
          <span class="context-value">{{ currentStage()?.name }}</span>
        </span>
      }

      <mat-menu #compactProjectsMenu="matMenu">
        @if (currentProject()?.name; as currentProjectName) {
          <div class="menu-current-project">
            <span class="menu-current-project__label">Current project</span>
            {{ currentProjectName }}
          </div>
        }
        @for (favProject of favouriteProjects$ | async; track favProject) {
          <button
            mat-menu-item
            [routerLink]="['projects', favProject.id, 'detail']"
          >
            {{ favProject.name }}
          </button>
        }
        @for (recentProject of recentProjects$ | async; track recentProject) {
          @if (recentProject.isMember) {
            <button
              mat-menu-item
              [routerLink]="['projects', recentProject.id, 'detail']"
            >
              {{ recentProject.name }}
            </button>
          }
        }
        <!-- Always present, so the menu can never open empty for a user with
             no current project, no favourites and no recents. -->
        <a mat-menu-item [routerLink]="['projects']">All projects</a>
      </mat-menu>
    </mat-toolbar-row>
  }

  @if (!compactNavigation()) {
    <mat-toolbar-row class="wide-toolbar-row" fxLayout="row">
      <button
        mat-button
        routerLink="/"
        routerLinkActive="active"
        class="logo-button"
        aria-label="SyRF home"
      >
        <mat-icon svgIcon="syrf-title" class="logo-icon" fxFlex="grow" />
      </button>
      @if (authenticated$ | async) {
        <button
          mat-button
          class="breadcrumb-root"
          [routerLink]="['projects']"
          data-testid="nav-projects-btn"
        >
          Projects
        </button>
      }
      @if ($underProjectRoute()) {
        <span class="separator">/</span>
        @if (authenticated$ | async) {
          <button
            class="recentProjects breadcrumb-segment"
            mat-button
            [attr.title]="currentProject()?.name"
            [matMenuTriggerFor]="projectsMenu"
            #projectsTrigger="matMenuTrigger"
            appHover
            #hover="hover"
          >
            <span class="project-name">{{ currentProject()?.name }}</span>
            <mat-icon iconPositionEnd>keyboard_arrow_down</mat-icon>
          </button>
        }
        <mat-menu #projectsMenu="matMenu">
          @if (currentProject()?.name; as currentProjectName) {
            <div class="menu-current-project">
              <span class="menu-current-project__label">Current project</span>
              {{ currentProjectName }}
            </div>
          }
          @for (favProject of favouriteProjects$ | async; track favProject) {
            <button
              mat-menu-item
              [routerLink]="['projects', favProject.id, 'detail']"
            >
              {{ favProject.name }}
            </button>
          }
          @for (recentProject of recentProjects$ | async; track recentProject) {
            <div>
              @if (recentProject.isMember) {
                <button
                  mat-menu-item
                  [routerLink]="['projects', recentProject.id, 'detail']"
                >
                  {{ recentProject.name }}
                </button>
              }
            </div>
          }
          <!-- Always present, so the menu can never open empty. -->
          <a mat-menu-item [routerLink]="['projects']">All projects</a>
        </mat-menu>
        @if ($underStageRoute()) {
          <span class="separator">/</span>
          <button
            mat-button
            class="stage-breadcrumb breadcrumb-segment"
            [attr.title]="currentStage()?.name"
          >
            <span class="stage-name">{{ currentStage()?.name }}</span>
          </button>
        }
      }
      @if ($underProjectRoute() === false) {
        <div @collapseInOut>
          <a
            mat-button
            class="nav-button"
            href="https://www.camarades.de"
            target="_blank"
            rel="noopener noreferrer"
            >Methodology
            <mat-icon
              iconPositionEnd
              class="externalLinkIcon"
              svgIcon="external-link-white"
              fxLayout="row"
              fxLayoutAlign="center start"
            />
          </a>
          <button mat-button [routerLink]="['mission']">Our Mission</button>
          <button
            [matMenuTriggerFor]="libraryMenu"
            mat-button
            matTooltip="Protocols & Systematic Review Library"
          >
            Library
          </button>
          <mat-menu #libraryMenu="matMenu">
            <button mat-menu-item [routerLink]="['library']">
              Systematic Review Library
            </button>
            <button mat-menu-item [routerLink]="['protocols']">
              Protocols
            </button>
          </mat-menu>
          <a
            rel="noopener noreferrer"
            mat-button
            class="nav-button"
            href="https://www.ed.ac.uk/clinical-brain-sciences/research/camarades/meet-the-team"
            target="_blank"
          >
            About Us
            <mat-icon
              iconPositionEnd
              class="externalLinkIcon"
              svgIcon="external-link-white"
              fxLayout="row"
              fxLayoutAlign="center start"
            />
          </a>
        </div>
      }

      <div class="menu-right" fxLayout="row">
        @if (appIsUnstable) {
          <button
            mat-button
            class="unstable-warning-btn"
            matTooltip="Application may be unstable due to an error. Consider refreshing the page."
            matTooltipClass="warning-tooltip"
            (click)="showErrorRecoveryDialog()"
          >
            <mat-icon color="warn" class="menuIcon warning-icon"
              >warning</mat-icon
            >
            Important!
          </button>
        }
        @if ($underProjectRoute()) {
          <div @appearDisappear>
            <button mat-button [matMenuTriggerFor]="resourcesMenu">
              <mat-icon class="menuIcon">topic</mat-icon>
              <span fxHide fxShow.gt-md="true">Resources</span>
              <mat-icon iconPositionEnd>keyboard_arrow_down</mat-icon>
            </button>
            <mat-menu #resourcesMenu="matMenu" color="accent">
              <a
                mat-menu-item
                href="https://www.camarades.de"
                target="_blank"
                rel="noopener noreferrer"
              >
                <div fxLayout="row" fxLayoutAlign="start">
                  Methodology
                  <mat-icon
                    class="externalLinkIcon"
                    svgIcon="external-link-black"
                  />
                </div>
              </a>
              <button mat-menu-item [routerLink]="['mission']">
                Our Mission
              </button>
              <button
                [matMenuTriggerFor]="librarySecondaryMenu"
                mat-menu-item
                matTooltip="Protocols & Systematic Review Library"
              >
                Library
              </button>
              <mat-menu #librarySecondaryMenu="matMenu">
                <button mat-menu-item [routerLink]="['library']">
                  Systematic Review Library
                </button>
                <button mat-menu-item [routerLink]="['protocols']">
                  Protocols
                </button>
              </mat-menu>
              <a
                mat-menu-item
                style="text-decoration: none"
                href="https://www.ed.ac.uk/clinical-brain-sciences/research/camarades/meet-the-team"
                target="_blank"
                rel="noopener noreferrer"
              >
                <div fxLayout="row" fxLayoutAlign="start">
                  About Us
                  <mat-icon
                    class="externalLinkIcon"
                    svgIcon="external-link-black"
                  />
                </div>
              </a>
            </mat-menu>
          </div>
        }

        <!-- Environment indicator chip (shown after banner is dismissed) -->
        <app-environment-chip
          (clicked)="openEnvironmentWarning()"
        ></app-environment-chip>

        <button mat-button [matMenuTriggerFor]="helpMenu">
          <mat-icon class="menuIcon">help</mat-icon>
          <span fxHide fxShow.gt-md="true">Help</span>
          <mat-icon iconPositionEnd>keyboard_arrow_down</mat-icon>
        </button>
        <mat-menu #helpMenu="matMenu">
          <button mat-menu-item [routerLink]="['faq']">
            Frequently Asked Questions
          </button>
          <a
            mat-menu-item
            [attr.href]="'' | userGuideUrl"
            target="_blank"
            rel="noopener noreferrer"
          >
            <div fxLayout="row" fxLayoutAlign="start">
              User Guide
              <mat-icon
                class="externalLinkIcon"
                svgIcon="external-link-black"
              />
            </div>
          </a>
          <button mat-menu-item [routerLink]="['about']">
            About Our Website
          </button>
          <button mat-menu-item (click)="openVersionInfo()">
            Version Information
          </button>
          <button mat-menu-item [routerLink]="['contact-us']">
            Contact Us
          </button>
        </mat-menu>

        @if (!(authenticated$ | async)) {
          <button
            id="auth-button"
            data-testid="nav-sign-in-btn"
            mat-button
            href="javascript:void(0);"
            (click)="signIn()"
            [disabled]="!(authIsIdle$ | async)"
          >
            <div fxLayout="row" fxLayoutAlign="space-between center">
              @if (authIsIdle$ | async) {
                <mat-icon class="menuIcon">fingerprint</mat-icon>
              }
              @if (!(authIsIdle$ | async)) {
                <mat-spinner diameter="20"></mat-spinner>
              }
              @if (authIsIdle$ | async) {
                <span>Sign In / Register</span>
              }
              @if (!(authIsIdle$ | async)) {
                <span>Signing In...</span>
              }
            </div>
          </button>
        }
        @if (authenticatedUser$ | async; as authenticatedUser) {
          <button
            mat-button
            [matMenuTriggerFor]="accountMenu"
            id="account-menu-trigger"
            data-testid="nav-account-menu"
          >
            @if (!(profilePictureUrl$ | async)) {
              <mat-icon class="menuIcon">fingerprint</mat-icon>
            }
            <div flexLayout="row" fxLayoutAlign="space-between center">
              @if (profilePictureUrl$ | async; as profilePicUrl) {
                <img
                  class="menu-avatar"
                  [attr.src]="profilePicUrl"
                  alt="profile picture"
                />
              }
              <span class="account-name" fxHide fxShow.gt-md="true"
                >Hello
                {{
                  authenticatedUser.fullName?.preferred ||
                    authenticatedUser.fullName?.first
                }}</span
              >
              @if (isImpersonatingUser$ | async) {
                <mat-icon
                  class="impersonating-icon"
                  matTooltip="Currently impersonating another user"
                  >supervisor_account</mat-icon
                >
              }
            </div>
            <mat-icon iconPositionEnd>keyboard_arrow_down</mat-icon>
          </button>
        }
        <mat-menu #accountMenu="matMenu">
          <a mat-menu-item routerLink="manage">My Account</a>
          @if (isAdmin$ | async) {
            <button
              mat-menu-item
              [mat-menu-trigger-for]="adminMenu"
              data-testid="desktop-admin-menu-trigger"
            >
              Admin
            </button>
          }
          <button mat-menu-item (click)="signOut()">Sign Out</button>
        </mat-menu>

        <mat-menu #adminMenu>
          <button mat-menu-item (click)="toggleAdminUiOptions()">
            {{ showingAdminUiOptions() ? 'Hide' : 'Show' }} Admin UI Options
          </button>
          <button mat-menu-item [routerLink]="['admin']">
            User Impersonation
          </button>
          <a
            mat-menu-item
            routerLink="/admin/feature-flags"
            data-testid="desktop-admin-feature-flags"
          >
            Feature Flags
          </a>
          <button mat-menu-item (click)="updateAllProjectInclusion()">
            Update All Project Inclusion
          </button>
          @if (currentProject()) {
            <button mat-menu-item (click)="updateAnnotationAnswerTally()">
              Update Annotation Answer Tally For Current Project
            </button>
          }
          @if (!(showDebuggers$ | async)) {
            <button mat-menu-item (click)="setDebuggers(true)">
              Debuggers
            </button>
          }
          @if (showDebuggers$ | async) {
            <button mat-menu-item (click)="setDebuggers(false)">
              Debuggers
              <mat-icon id="menu-check">check</mat-icon>
            </button>
          }
          @if (!(showLogging$ | async)) {
            <button mat-menu-item (click)="setLogging(true)">
              Diagnostics
            </button>
          }
          @if (showLogging$ | async) {
            <button mat-menu-item (click)="setLogging(false)">
              Diagnostics
              <mat-icon class="menu-check">check</mat-icon>
            </button>
          }
          @if (!(showChangeDetection$ | async)) {
            <button
              mat-menu-item
              (click)="setChangeDetectionVisualisation(true)"
            >
              Change Detection Visualisation
            </button>
          }
          @if (showChangeDetection$ | async) {
            <button
              mat-menu-item
              (click)="setChangeDetectionVisualisation(false)"
            >
              Change Detection Visualisation
              <mat-icon class="menu-check">check</mat-icon>
            </button>
          }
          <button mat-menu-item (click)="openGuidDecoder()">
            Guid Decoder/Encoder...
          </button>
          <button mat-menu-item (click)="onThrowError()">Throw Error</button>
          <button mat-menu-item disabled>
            {{ randomId }}
          </button>
        </mat-menu>
      </div>
    </mat-toolbar-row>
  }
</mat-toolbar>
